/**
 * Active-portal accessor.
 *
 * The portal layer is selected by the SAME build variable as branding
 * (`VITE_APP_BRAND`); rather than introduce a second selector it reuses
 * `activeBrandKey()` from `@/config/brand`. There is no runtime portal switching.
 */
import { activeBrandKey } from '@/config/brand'

import { FEATURE_CATALOG } from './feature.catalog'
import { PORTALS } from './portal.registry'
import type { BrandKey, FeatureKey, IPortalConfig } from './portal.types'

/** Resolve the active portal key — a thin alias over the brand selector. */
export function activePortalKey(): BrandKey {
  return activeBrandKey()
}

/** Resolve the full config for the active portal. */
export function getActivePortal(): IPortalConfig {
  return PORTALS[activePortalKey()]
}

/**
 * Resolve the display label for a feature: the portal's nav override if present,
 * otherwise the catalog default. Defaults to the active portal's config.
 */
export function resolveNavLabel(
  feature: FeatureKey,
  cfg: IPortalConfig = getActivePortal(),
): string {
  const override = cfg.nav.find((item) => item.feature === feature)?.label
  return override ?? FEATURE_CATALOG[feature].defaultLabel
}

/**
 * Resolve the page heading / document title for a feature: the portal's
 * `pageTitles` override if present, otherwise the catalog default. Use this for
 * page `<h1>`s and the guard's `document.title` so a portal can word a page
 * differently (e.g. DUNE "Software Packages" vs Peraso "Legacy Software
 * Packages") without the wording being restated in each view.
 */
export function resolvePageTitle(
  feature: FeatureKey,
  cfg: IPortalConfig = getActivePortal(),
): string {
  // `home` is not overridable (see IPortalConfig.pageTitles) — and so is not a key
  // of that map — so it short-circuits straight to the catalog default.
  const override = feature === 'home' ? undefined : cfg.pageTitles?.[feature]
  return override ?? FEATURE_CATALOG[feature].defaultPageTitle
}

/** Convenience singleton for app code (portal is fixed per build). */
export const portal: IPortalConfig = getActivePortal()
